import {
  type CreateGameBody,
  GameDto,
  InviteDto,
  MeDto,
  MyGamesDto,
  type UpdateMeBody,
} from '@brig/api-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, use } from 'react';
import type { ApiClient } from './client.ts';
import type { LiveFeed } from './live.ts';

export const ApiContext = createContext<ApiClient | null>(null);
export const LiveContext = createContext<LiveFeed | null>(null);

export function useLive(): LiveFeed {
  const live = use(LiveContext);
  if (!live) throw new Error('useLive must be used inside <LiveContext>');
  return live;
}

export function useApi(): ApiClient {
  const api = use(ApiContext);
  if (!api) throw new Error('useApi must be used inside <ApiContext>');
  return api;
}

export const queryKeys = {
  me: ['me'] as const,
  myGames: ['me', 'games'] as const,
  game: (id: string) => ['games', id] as const,
  invite: (code: string) => ['invites', code] as const,
};

export function useMe(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: ({ signal }) => api.get('/api/me', MeDto, signal),
    enabled,
    staleTime: 60_000,
  });
}

export function useUpdateMe() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: UpdateMeBody) => api.patch('/api/me', patch, MeDto),
    onSuccess: (me) => {
      queryClient.setQueryData(queryKeys.me, me);
    },
  });
}

export function useMyGames() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.myGames,
    queryFn: ({ signal }) => api.get('/api/me/games', MyGamesDto, signal),
    staleTime: 15_000,
  });
}

/**
 * One game as the server keeps it. Read once per screen: from then on the game screen holds
 * its own copy and keeps it in step with every answer (game/server.ts).
 */
export function useGame(id: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.game(id ?? ''),
    queryFn: ({ signal }) => api.get(`/api/games/${id ?? ''}`, GameDto, signal),
    enabled: id !== null,
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/** An invite as its link shows it; readable signed out. */
export function useInvite(code: string) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.invite(code),
    queryFn: ({ signal }) => api.get(`/api/invites/${encodeURIComponent(code)}`, InviteDto, signal),
    retry: false,
  });
}

/** Takes an invite: the game begins, with the player on the blue side. */
export function useAcceptInvite() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api.post(`/api/invites/${encodeURIComponent(code)}/accept`, {}, GameDto),
    onSuccess: (game) => {
      queryClient.setQueryData(queryKeys.game(game.id), game);
      void queryClient.invalidateQueries({ queryKey: queryKeys.myGames });
    },
  });
}

/** Starts a new game. */
export function useCreateGame() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateGameBody) => api.post('/api/games', body, GameDto),
    onSuccess: (game) => {
      queryClient.setQueryData(queryKeys.game(game.id), game);
      void queryClient.invalidateQueries({ queryKey: queryKeys.myGames });
    },
  });
}
